// src/components/business/BusinessCard.tsx
//
// Card de negócio da listagem /businesses — réplica literal do wireframe
// seção 04 (.bizc): topo logo + botão upload · nome · meta (tipo + status) ·
// stats (nº demandas, nº repos). Classes em src/styles/wf-biz.css.
//
// C4 (correção v2): contagens vêm do payload do backend
// (business.demands_count / business.repos_count) — sem fetch client-side.

import { Eye, Trash2 } from 'lucide-react';
import type { Business } from '@/lib/api';
import { WfKebabMenu, type WfKebabEntry } from '@/components/ui/WfKebabMenu';
import { BusinessLogo, BusinessLogoUploadButton } from './BusinessLogo';
import { LIFECYCLE_LABELS } from './business-constants';

interface BusinessCardProps {
  business: Business;
  onClick: (business: Business) => void;
  onUpdated: (business: Business) => void;
  onError?: (message: string) => void;
  /** Abre a confirmação de exclusão (BusinessDeleteDialog na page). */
  onDelete: (business: Business) => void;
}

/** Stat do card (wireframe: `<b>12</b> demandas`). undefined (backend antigo) → "—". */
function StatCount({ count, singular, plural }: { count?: number; singular: string; plural: string }) {
  if (count === undefined) return <span>— {plural}</span>;
  return (
    <span>
      <b>{count}</b> {count === 1 ? singular : plural}
    </span>
  );
}

export function BusinessCard({ business, onClick, onUpdated, onError, onDelete }: BusinessCardProps) {
  const menuItems: WfKebabEntry[] = [
    { key: 'details', label: 'Ver detalhes', icon: Eye, onSelect: () => onClick(business) },
    'sep',
    { key: 'delete', label: 'Excluir', icon: Trash2, danger: true, onSelect: () => onDelete(business) },
  ];

  return (
    <div
      className="wf-bizc"
      role="button"
      tabIndex={0}
      onClick={() => onClick(business)}
      onKeyDown={(e) => {
        // Mesma técnica do fix do espaço no rename de sessões (§5.2.2):
        // teclas que borbulham de filhos (kebab/upload) NÃO ativam o card.
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(business);
        }
      }}
    >
      {/* Topo: logo à esquerda + upload e kebab à direita (wireframe .bizc-top) */}
      <div className="wf-bizc-top">
        <BusinessLogo business={business} />
        <span
          className="wf-bizc-actions"
          // O card é role=button com handler próprio — sem segurar o evento
          // aqui, abrir o kebab navega pro detalhe (clique E keydown).
          // BIZ-01: SÓ stopPropagation — preventDefault aqui cancelava a ação
          // default do clique programático no <input type=file> escondido do
          // upload (que borbulha por este wrapper) e o file picker nunca abria.
          onClick={(e) => {
            e.stopPropagation();
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <BusinessLogoUploadButton business={business} onUpdated={onUpdated} onError={onError} />
          <WfKebabMenu ariaLabel={`Ações do negócio ${business.name}`} items={menuItems} />
        </span>
      </div>

      {/* Nome (wireframe .bizname) */}
      <div className="wf-bizname">{business.name}</div>

      {/* Meta: tipo/subtipo + status (wireframe .bizmeta — mostra o valor cru,
          ex: "product"/"saas", como o mockup) */}
      <div className="wf-bizmeta">
        <span className="wf-biztype">{business.subtype || business.type}</span>
        <span className={`wf-bizst ${business.lifecycle_status}`}>
          {LIFECYCLE_LABELS[business.lifecycle_status] ?? business.lifecycle_status}
        </span>
      </div>

      {/* Stats: nº demandas + nº repos (wireframe .bizstats) */}
      <div className="wf-bizstats">
        <StatCount count={business.demands_count} singular="demanda" plural="demandas" />
        <StatCount count={business.repos_count} singular="repo" plural="repos" />
      </div>
    </div>
  );
}
